/**
 * Stages S2, S2b and S7 — macro definition harvesting and macro expansion.
 *
 * Source:  (`extractMacros`, `expandMacros`).
 * Spec:   docs/spec/00-MASTER-SPEC.md §1 (pipeline), §3.2 (R-007 … R-018),
 *         docs/spec/01-preamble-settings-macros-fonts.md §4 and §6.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS STAGE DECIDES MODE A vs MODE B.
 *
 * In production (`ConverterApp.tsx:112`) the reference preamble is concatenated
 * ahead of the document, so S2 harvests its 43 `\newcommand` bodies and S7 rewrites
 * every domain macro (`\Nverset`, `\citation`, `\ts`, `\appel*` …) into generic
 * LaTeX **before** `processCommands` (S11) runs. The hand-written Bahá'í handlers
 * at  therefore never fire — that is **Mode A**.
 *
 * With an empty preamble box there are no macros, `expandMacros` is a near no-op,
 * and those handlers do fire — that is **Mode B**. Both are correct; the mode is a
 * property of the input, not of this module. Nothing here branches on it.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * G-15 / DF-1 / DF-2 — FIXED 2026-08-30, IN THE PREAMBLE, NOT HERE.
 *
 * This module has no `\newif` / `\ifpandoc` / `\else` / `\fi` evaluation, and must
 * not grow one. While `preambuleV3.tex` still wrapped its note definitions in an
 * `\ifpandoc`, both branches were harvested as ordinary duplicates and the FIRST
 * won (R-017) — always the Pandoc one. `\afficherEndNotes` expanded to the empty
 * string and the translator's notes block vanished (DF-1); `\Nextrait` emitted a
 * literal `\verb|<TAB>|` (DF-2).
 *
 * A stage that deleted the losing branch before S2 was tried on 2026-08-29 and
 * removed the next day. It worked, and it also destroyed information: the only
 * `\renewcommand{\thefootnote}` in the preamble sat in the branch it cut, so
 * footnote numbering silently disappeared and published as `‡‡`. `\ifpandoc` is
 * Pandoc's switch; it was removed from the preamble instead, which is where it
 * did not belong. **Nothing may remove anything from the preamble before S2.**
 *
 * FIDELITY: the remaining defects here are still reproduced deliberately, in order
 * of how much damage they do:
 *
 *   G-23 / RK-16 `\newcommand*`, optional-argument `\newcommand{\x}[1][d]{…}` and
 *                parameterised `\def\x#1{…}` are silently dropped — and, because
 *                the stripping walk re-uses the same two regexes, their raw text is
 *                then PUBLISHED as document body.
 *   G-24 / DF-8  `#n` substitution and zero-arg expansion both go through
 *                `String.prototype.replace` with a **string** replacement, so `$&`,
 *                `` $` ``, `$'` and `$$` inside an argument (or inside a definition)
 *                are interpreted as JS replacement patterns and corrupt the text.
 *   G-22 / RK-17 Every macro is matched CASE-INSENSITIVELY (`gi`), so `\TS`,
 *                `\nverset` and `\CITATION` all expand to the definition they
 *                merely resemble.
 *   G-30 / DF-18 The definition-stripping walk is O(n²) in the source. The port uses
 *                an anchored scan that is byte-identical but linear — the ONLY
 *                deviation in this file, and it is unobservable in the output.
 *
 * Pure functions, no I/O, no module-level mutable state (the regex **sources** are
 * shared, the `RegExp` objects — which carry a mutable `lastIndex` — are not).
 */

import { extractBracedContent } from './braces'
import type { MacroDefinition } from './types'

/* ─────────────────────────────── regex sources ───────────────────────────── */

/**
 * R-007 / R-008 — `\newcommand{\name}[n]{def}`, `\newcommand\name[n]{def}`,
 * `\renewcommand…`. Verbatim from; re-used anchored by the stripping walk
 * at.
 *
 * `\{?` and `\}?` are independently optional, so `\renewcommand\name{…}` and even
 * the malformed `\renewcommand{\name…` are accepted (spec 01 §4.1).
 *
 * R-010 / G-23 — note what is NOT here: no `\*` after `command`, and no second
 * `[…]` group for a default value. `\newcommand*` never matches at all;
 * `\newcommand{\x}[1][d]{…}` matches the header but leaves the cursor on `[`, so
 * `extractBracedContent` returns null and the definition is dropped.
 */
const NEWCOMMAND_SOURCE = String.raw`\\(?:new|renew)command\s*\{?\\(\w+)\}?\s*(?:\[(\d+)\])?\s*`

/**
 * R-009 — `\def\name{def}`. Verbatim from; re-used anchored at.
 * `numArgs` is forced to 0.
 *
 * R-010 / G-23 — the pattern stops before `#`, so `\def\x#1{…}` leaves the cursor
 * on `#` and the definition is dropped.
 */
const DEF_SOURCE = String.raw`\\def\\(\w+)\s*`

/**
 * R-016 — hard cap on expansion passes.
 *
 * NOT a depth limit. Each pass replaces every macro globally, so a chain 12 deep
 * still resolves completely: pass 1 rewrites `\niveauA`→`\niveauB` everywhere, and
 * by the time the loop reaches `\niveauB` **in the same pass** the token is already
 * there to be rewritten again. What the cap bounds is the number of times the whole
 * macro list is re-walked, which only matters when a later macro reintroduces an
 * earlier one.
 */
export const MAX_EXPANSION_PASSES = 10

/* ──────────────────────────── S2 — extractMacros ─────────────────────────── */

/**
 * S2 — harvest macro definitions from the **whole** cleaned document, preamble
 * included.
 *
 * Two sequential scans appended to one array, so **every `\def` lands after every
 * `\newcommand`** regardless of source position (spec 01 §4.2). That ordering is
 * load-bearing: expansion runs in array order and the first definition of a name
 * consumes every occurrence (R-017), so a `\newcommand` beats a `\def` of the same
 * name even when the `\def` came first in the file.
 *
 * Definitions are never de-duplicated, and where a duplicate exists the first wins
 * (R-017). The four pairs the reference preamble yields here (`Nextrait`, `note`,
 * `afficherEndNotes`, `afficherComTrad`) are the two sides of an `\ifpandoc`, and
 * first-wins picks the Pandoc side — which is DF-1 and DF-2. `preambuleV3.tex` no
 * longer contains those conditionals, so with the current preamble each of those
 * names arrives here exactly once. The read-only reference still has them.
 *
 * @param latex the comment-stripped document (preamble + body)
 * @returns definitions in harvest order, duplicates preserved
 */
export function extractMacros(latex: string): MacroDefinition[] {
  const macros: MacroDefinition[] = []

  // R-007/R-008. The scan does not skip definition bodies, so a
  // `\renewcommand` nested inside another command's braces is harvested too —
  // that is how `\contentsname` (pre:113, inside `\addto\captionsfrench{…}`)
  // becomes macro #3 of the reference preamble.
  const cmdRegex = new RegExp(NEWCOMMAND_SOURCE, 'g')
  let match: RegExpExecArray | null
  while ((match = cmdRegex.exec(latex)) !== null) {
    // Group 1 is mandatory in the pattern; `?? ''` only satisfies
    // noUncheckedIndexedAccess and is unreachable.
    const name = match[1] ?? ''
    const numArgs = match[2] ? parseInt(match[2], 10) : 0
    const afterMatch = match.index + match[0].length

    const braced = extractBracedContent(latex, afterMatch)
    if (braced) {
      macros.push({ name, numArgs, definition: braced.content })
    }
    // R-010/G-23: no `else`. A header whose body cannot be brace-matched is
    // silently discarded, and `lastIndex` has already advanced past the header,
    // so the scan resumes *inside* the unparsed body.
  }

  // R-009. Appended AFTER scan A — see the ordering note above.
  const defRegex = new RegExp(DEF_SOURCE, 'g')
  while ((match = defRegex.exec(latex)) !== null) {
    const name = match[1] ?? ''
    const afterMatch = match.index + match[0].length

    const braced = extractBracedContent(latex, afterMatch)
    if (braced) {
      macros.push({ name, numArgs: 0, definition: braced.content })
    }
  }

  return macros
}

/* ────────────────────────── S2b — manualMacros filter ────────────────────── */

/**
 * R-011 — the `manualMacros` exclusion set.
 *
 * It is **empty**, and that single fact is what makes Mode A the production
 * reality: because nothing is excluded, every domain macro is expanded into generic
 * LaTeX at S7 and the hand-written Bahá'í handlers at  are unreachable
 * whenever a preamble is supplied.
 *
 * The source comment reads: "All macros handled generically via preamble
 * expansion".
 */
export const MANUAL_MACROS: ReadonlySet<string> = new Set<string>([])

/**
 * S2b — drop macros the command stage wants to handle by hand.
 *
 * With {@link MANUAL_MACROS} empty this is an identity filter. It is kept as a
 * distinct step because it is a distinct pipeline stage in the spec, and because
 * populating the set is the proposed fix for DF-15 (decision D-B) — which is NOT
 * applied here.
 */
export function filterManualMacros(
  macros: readonly MacroDefinition[],
  manualMacros: ReadonlySet<string> = MANUAL_MACROS,
): MacroDefinition[] {
  return macros.filter((m) => !manualMacros.has(m.name))
}

/* ─────────────────────── S7 — stripping walk + expansion ─────────────────── */

/**
 * S7, stage A — remove macro definitions from the body.
 *
 * At each index the walk asks "does a `\newcommand`/`\renewcommand`/`\def` header
 * start here, and is it followed by a brace group?". If yes the whole definition is
 * skipped; otherwise one character is copied and the walk advances by one.
 *
 * G-23 / RK-16 — **the defect.** The walk re-uses the extractor's patterns, so
 * exactly the forms S2 could not parse are also the forms this cannot remove:
 * `\newcommand*{\x}{…}`, `\newcommand{\x}[1][d]{…}` and `\def\x#1{…}` are left in
 * place and published as document text. When a header matches but the brace group
 * does not, the walk copies a single `\` and moves on; the same header cannot match
 * again from inside itself, so the rest of that definition survives verbatim —
 * except for any well-formed definition nested inside it, which IS stripped, tearing
 * a hole in the text that then gets published.
 *
 * G-30 / DF-18 — the source is O(n²): `result.substring(i)` allocates a fresh
 * string per character and `cleaned += result[i]` appends one character at a time.
 * This implementation is linear (sticky match + slice accumulation) and produces
 * byte-identical output. Sticky matching at index `i` is exactly equivalent to
 * `^`-matching on `substring(i)`: neither pattern uses the `m` flag or a lookbehind,
 * so `^` can only ever match at offset 0 of that substring.
 *
 * @param text the document body (S5 has already discarded the preamble)
 */
export function stripMacroDefinitions(text: string): string {
  // `y` (sticky) instead of `^` on a fresh substring — see the O(n²) note above.
  const cmdStrip = new RegExp(NEWCOMMAND_SOURCE, 'y')
  const defStrip = new RegExp(DEF_SOURCE, 'y')

  let cleaned = ''
  let copyStart = 0
  let i = 0

  while (i < text.length) {
    // Both patterns begin with a literal `\`, so no other character can start a
    // match. Pure short-circuit; no behavioural effect.
    if (text.charCodeAt(i) === 0x5c /* \ */) {
      cmdStrip.lastIndex = i
      const cmdMatch = cmdStrip.exec(text)

      if (cmdMatch) {
        const braced = extractBracedContent(text, i + cmdMatch[0].length)
        if (braced) {
          cleaned += text.slice(copyStart, i)
          i = braced.endIndex + 1
          copyStart = i
          continue
        }
        // G-23: header matched, body did not. Fall through and copy one char.
      } else {
        // `else if (defMatch)` in the source: the `\def` branch is only
        // consulted when the `\newcommand` pattern did not match at all.
        defStrip.lastIndex = i
        const defMatch = defStrip.exec(text)
        if (defMatch) {
          const braced = extractBracedContent(text, i + defMatch[0].length)
          if (braced) {
            cleaned += text.slice(copyStart, i)
            i = braced.endIndex + 1
            copyStart = i
            continue
          }
        }
      }
    }

    // Source: `cleaned += result[i]; i++`. Here the character stays inside the
    // pending `[copyStart, …)` run and is flushed by the next slice.
    i++
  }

  cleaned += text.slice(copyStart)
  return cleaned
}

/**
 * S7 — expand custom macros in the document body.
 *
 * Two stages: {@link stripMacroDefinitions}, then up to {@link MAX_EXPANSION_PASSES}
 * passes over the macro list.
 *
 * R-017 — **the first definition of a name always wins.** Macros are walked in array
 * order and each replacement is global, so by the time a duplicate is reached there
 * is nothing left for it to match. This still governs genuine duplicates.
 *
 * That rule is also what decided the `\ifpandoc` branches while the preamble still
 * had them, and it always picked the Pandoc side: `\afficherEndNotes` became the
 * empty string and the notes block vanished (DF-1); `\note` became `\footnote`;
 * `\Nextrait` emitted a literal `\verb|<TAB>|` (DF-2). `preambuleV3.tex` no longer
 * carries those conditionals, so the question no longer arises with it.
 *
 * @param latex the document body, post-S5
 * @param macros definitions from {@link extractMacros}, post-{@link filterManualMacros}
 */
export function expandMacros(latex: string, macros: readonly MacroDefinition[]): string {
  // PREAMBLE LINK: \ifpandoc / \else / \fi — REMOVED FROM preambuleV3.tex on 2026-08-30, because
  // it is Pandoc's switch and an HTML target is not Pandoc. NOT HANDLED HERE, and must not be:
  // evaluating a conditional means discarding a branch, and discarding anything the preamble sets
  // is what published `‡‡` for every footnote past the ninth. The preamble now states both
  // numbering rules outright (\thefootnote AND \theendnote), because the DOCUMENT chooses per call
  // site whether it writes \footnote{} or \endnote{}. See the `% CONVERTISSEUR:` note on the notes
  // block in preambuleV3.tex. A preamble that still contains \ifpandoc (the read-only reference,
  // or an old record in D1) falls back to R-017 first-wins and reproduces DF-1 and DF-2.
  // R-012 — stage A.
  let result = stripMacroDefinitions(latex)

  // R-016 — stage B.
  for (let iteration = 0; iteration < MAX_EXPANSION_PASSES; iteration++) {
    let changed = false

    for (const macro of macros) {
      if (macro.numArgs === 0) {
        // R-013 — zero-arg expansion.
        // G-22/RK-17: `gi`, so `\ARBSQ` and `\Arbsq` both expand via `\arbsq`.
        // `(?![a-zA-Z])` stops `\note` firing inside `\notebook` — but it does NOT
        // stop it firing before a digit or `_`.
        const regex = new RegExp(`\\\\${macro.name}(?![a-zA-Z])`, 'gi')
        // G-24 (spec 01 §6.2, D-11): `macro.definition` is a **replacement
        // string**, so `$$`, `$&`, `` $` `` and `$'` inside a DEFINITION are
        // interpreted too — a definition of `$$E=mc^2$$` emits `$E=mc^2$`, silently
        // downgrading display math to inline. The master spec's G-24/R-015 rows
        // describe only the argument path; this half is spec 01 D-11.
        const newResult = result.replace(regex, macro.definition)
        if (newResult !== result) changed = true
        result = newResult
      } else {
        // R-014 — n-arg expansion. One occurrence at a time, so that a
        // same-name macro nested inside an argument is not also matched during this
        // pass (`pattern.lastIndex = pos`, ); it is picked up next pass.
        //
        // The guard is `(?=\s*\{)` only — there is no `(?![a-zA-Z])` here. Prefix
        // collisions are prevented solely by requiring `{` next, which is why
        // `\appelCR` does not swallow `\appelCRS{…}`. Combined with `gi` this is
        // still unsafe across case: `\CITATION{x}` matches `\citation`.
        const pattern = new RegExp(`\\\\${macro.name}(?=\\s*\\{)`, 'gi')
        let newResult = ''
        let lastIndex = 0
        let match: RegExpExecArray | null

        while ((match = pattern.exec(result)) !== null) {
          // Inside text a previous expansion already consumed.
          if (match.index < lastIndex) {
            continue
          }

          newResult += result.substring(lastIndex, match.index)

          const args: string[] = []
          let pos = match.index + match[0].length

          // Skip whitespace between the macro name and its first argument
          //. Duplicated by the per-argument skip below — harmless.
          while (pos < result.length && /\s/.test(result.charAt(pos))) pos++

          for (let argNum = 0; argNum < macro.numArgs; argNum++) {
            while (pos < result.length && /\s/.test(result.charAt(pos))) pos++
            const braced = extractBracedContent(result, pos)
            if (braced) {
              args.push(braced.content)
              pos = braced.endIndex + 1
            } else {
              break
            }
          }

          if (args.length === macro.numArgs) {
            let expanded = macro.definition
            // R-015 / G-24 / DF-8 — sequential, not simultaneous: `#1` is
            // substituted before `#2`, so a literal `#2` that arrived inside
            // argument 1 is then substituted as well, and `#10` is unreachable.
            //
            // `arg` is a **replacement string**: `$&` inserts the matched `#n`,
            // `` $` `` everything before it, `$'` everything after, `$$` a single
            // `$`. A verse containing `$$` is a realistic trigger. DO NOT change
            // this to a replacer function — that is the deferred fix for DF-8.
            //
            // `args.length === macro.numArgs` here, so iterating `args` is exactly
            // the source's `for (i = 0; i < macro.numArgs; i++)`.
            args.forEach((arg, argIndex) => {
              expanded = expanded.replace(new RegExp(`#${argIndex + 1}`, 'g'), arg)
            })
            newResult += expanded
            lastIndex = pos
            changed = true
            pattern.lastIndex = pos
          } else {
            // Fewer braced groups than the arity demands: keep the original token
            // as literal text. This is how a bare `\Nverset` with no
            // argument survives S7 and reaches `processCommands`.
            newResult += match[0]
            lastIndex = match.index + match[0].length
          }
        }

        newResult += result.substring(lastIndex)
        result = newResult
      }
    }

    if (!changed) break
  }

  return result
}
